import { Koi } from '@ksyos/koi';
import type { GradingSummary } from '@common/enums/gradingSummary';
import { ServiceCode } from '@common/enums/serviceCode';
import type { ISchemaContainer } from './schemaContainer';
import { ProcessEntity } from '../enums/processEntity';
import { tcohV2FinalDiagnosticReportAddAddendumSchema } from './customControls/cardiology/holterConsult/cardiology';
import { tfoV1FinalDiagnosticReport } from './customControls/ophthalmology';
import { cloneAndModifySchema } from './utils/cloneAndModifySchema';

export type Addendum = {
    id: string;
    processEntity: ProcessEntity;
    properties: Record<string, string>;
};

export type OsasAddendumRecord = {
    createdAt: string;
    properties: Record<'message', string>;
};

export type AddAddendumRequestBody = {
    addendum: Addendum;
    serviceCode: (typeof servicesWithAddendum)[number];
};

export type CustomControlDataAddendum = {
    createdAt: Date;
    gradingSummary?: GradingSummary;
    properties: Record<string, unknown>;
};

const osasAddAddendum = Koi.object({
    message: Koi.string().required().min(1),
});

export const servicesWithAddendum = [
    ServiceCode.TCOH,
    ServiceCode.TFO,
    ServiceCode.OSASC1,
    ServiceCode.OSASC2,
    ServiceCode.OSASVC,
] as const;

export const addendumValidationSchema: ISchemaContainer<Addendum> = {
    schema: Koi.object({
        id: Koi.string().uuid(),
        processEntity: Koi.koi().enum(ProcessEntity),
        properties: Koi.when(Koi.ref('/serviceCode'), {
            otherwise: osasAddAddendum,
            switch: [
                {
                    is: ServiceCode.TCOH,
                    then: cloneAndModifySchema(tcohV2FinalDiagnosticReportAddAddendumSchema.schema, {
                        makeAllOptional: true,
                    }),
                },
                {
                    is: ServiceCode.TFO,
                    then: cloneAndModifySchema(tfoV1FinalDiagnosticReport, { makeAllOptional: true }),
                },
            ],
        }),
    }).options({ convert: false, presence: 'required' }),
};

export const addAddendumSchema: ISchemaContainer<AddAddendumRequestBody> = {
    schema: Koi.object({
        addendum: addendumValidationSchema.schema,
        serviceCode: Koi.string()
            .valid(...servicesWithAddendum)
            .required(),
    }).options({ convert: false, presence: 'required' }),
};
